import { useEffect, useState } from 'react';
import { Shield, AlertTriangle, Lock, Activity, FileText } from 'lucide-react';

interface EscapeRouteData {
  id: number;
  data: any;
  created_at: string;
  created_by: number;
}

export default function EscapeRoutePage() {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EscapeRouteData[]>([]);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const response = await fetch('/api/escaperoute/ai/status', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to check access');
      }

      const result = await response.json();
      setHasAccess(result.hasAccess);

      if (result.hasAccess) {
        await loadData();
      }
    } catch (err) {
      setError('Failed to verify access permissions');
      console.error('Access check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const response = await fetch('/api/escaperoute/data', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load data');
      }

      const result = await response.json();
      setData(result.data || []);
    } catch (err) {
      setError('Failed to load EscapeRoute data');
      console.error('Data load error:', err);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      }}>
        <div style={{
          textAlign: 'center',
          color: '#00F0FF',
        }}>
          <Activity style={{ width: 48, height: 48, margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
          <p style={{ fontSize: '16px', fontFamily: 'Rajdhani, sans-serif' }}>
            Verifying Access...
          </p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        padding: '24px',
      }}>
        <div style={{
          maxWidth: '600px',
          background: 'rgba(255, 0, 0, 0.1)',
          border: '2px solid #FF0000',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
        }}>
          <Lock style={{
            width: 64,
            height: 64,
            margin: '0 auto 24px',
            color: '#FF0000',
          }} />
          <h1 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '32px',
            fontWeight: 700,
            color: '#FF0000',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            margin: '0 0 16px 0',
            textShadow: '0 0 20px rgba(255, 0, 0, 0.6)',
          }}>
            ACCESS DENIED
          </h1>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '16px',
            color: '#8A95AA',
            margin: '0 0 24px 0',
            letterSpacing: '1px',
          }}>
            You do not have permission to access the BGS EscapeRoute module.
            Please contact your system administrator if you believe this is an error.
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px',
            background: 'rgba(255, 255, 0, 0.1)',
            border: '1px solid #FFD93D',
            borderRadius: '6px',
          }}>
            <AlertTriangle style={{ width: 16, height: 16, color: '#FFD93D' }} />
            <span style={{
              color: '#FFD93D',
              fontSize: '14px',
              fontFamily: 'Rajdhani, sans-serif',
            }}>
              This access attempt has been logged
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif',
    }}>
      {/* Hero Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '48px',
        borderBottom: '2px solid #00FF00',
        paddingBottom: '32px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '16px',
        }}>
          <Shield style={{
            width: 48,
            height: 48,
            color: '#00FF00',
            filter: 'drop-shadow(0 0 10px rgba(0, 255, 0, 0.6))',
          }} />
          <h1 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '48px',
            fontWeight: 700,
            color: '#00FF00',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            margin: 0,
            textShadow: '0 0 20px rgba(0, 255, 0, 0.6)',
          }}>
            ESCAPEROUTE
          </h1>
        </div>
        <p style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '16px',
          color: '#8A95AA',
          maxWidth: '800px',
          margin: '0 auto 24px auto',
          letterSpacing: '1px',
        }}>
          Restricted Access Module - Authorized Personnel Only
        </p>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(0, 255, 0, 0.1)',
          border: '2px solid #00FF00',
          borderRadius: '6px',
          padding: '8px 16px',
          boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)',
        }}>
          <Shield style={{ width: 16, height: 16, color: '#00FF00' }} />
          <span style={{
            color: '#00FF00',
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}>
            ACCESS GRANTED
          </span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          background: 'rgba(255, 0, 0, 0.1)',
          border: '2px solid #FF0000',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <AlertTriangle style={{ width: 24, height: 24, color: '#FF0000' }} />
          <span style={{ color: '#FF0000', fontSize: '14px' }}>{error}</span>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.03)',
        border: '2px solid rgba(0, 255, 0, 0.3)',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
        }}>
          <FileText style={{ width: 24, height: 24, color: '#00F0FF' }} />
          <h2 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#00F0FF',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: 0,
          }}>
            EscapeRoute Data
          </h2>
        </div>

        {data.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px',
            color: '#8A95AA',
          }}>
            <p style={{ fontSize: '16px', margin: 0 }}>
              No data available. This module is under construction.
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gap: '16px',
          }}>
            {data.map((item) => (
              <div
                key={item.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  borderRadius: '8px',
                  padding: '16px',
                }}
              >
                <pre style={{
                  color: '#8A95AA',
                  fontSize: '14px',
                  margin: 0,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(item, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status Footer */}
      <div style={{
        marginTop: '32px',
        padding: '16px',
        background: 'rgba(0, 255, 0, 0.05)',
        border: '1px solid rgba(0, 255, 0, 0.2)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Activity style={{ width: 16, height: 16, color: '#00FF00' }} />
          <span style={{
            color: '#00FF00',
            fontSize: '12px',
            fontFamily: 'Rajdhani, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}>
            System Operational
          </span>
        </div>
        <div style={{
          color: '#8A95AA',
          fontSize: '12px',
          fontFamily: 'Rajdhani, sans-serif',
        }}>
          All activities are monitored and logged
        </div>
      </div>
    </div>
  );
}
